import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInvitation, useInvitationGuests } from '@/hooks/useInvitations';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Users,
  Copy,
  ExternalLink,
  Check,
  Clock,
  UserPlus,
  Send,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import CSVImportModal from '@/components/dashboard/CSVImportModal';
import { confirmedAttendees } from '@/lib/guests/headcount';
import { useMarkLinkSent } from '@/hooks/useGuests';

const GuestManager = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const { data: invitation, isLoading: loadingInvitation } = useInvitation(id || '');
  const { data: guests, isLoading: loadingGuests } = useInvitationGuests(id || '');
  const [importOpen, setImportOpen] = useState(false);
  const markLinkSent = useMarkLinkSent();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  const copyInviteLink = (guest: { id: string; unique_access_code: string }) => {
    const link = `${window.location.origin}/i/${invitation?.unique_slug}?code=${guest.unique_access_code}`;
    navigator.clipboard.writeText(link);
    markLinkSent.mutate(guest.id); // 008 · US3
    toast.success('Link copiado al portapapeles');
  };

  const shareViaWhatsApp = (guest: { id: string; guest_name: string; guest_phone?: string | null; unique_access_code: string }) => {
    const link = `${window.location.origin}/i/${invitation?.unique_slug}?code=${guest.unique_access_code}`;
    const message = `¡Hola ${guest.guest_name}! Te invitamos a nuestra boda. Confirma tu asistencia aquí: ${link}`;
    window.open(`https://wa.me/${guest.guest_phone?.replace(/\D/g, '') || ''}?text=${encodeURIComponent(message)}`, '_blank');
    markLinkSent.mutate(guest.id); // 008 · US3
  };

  const stats = {
    total: guests?.length || 0,
    confirmed: guests?.filter(g => g.rsvp_status === 'confirmed').length || 0,
    declined: guests?.filter(g => g.rsvp_status === 'declined').length || 0,
    pending: guests?.filter(g => g.rsvp_status === 'pending').length || 0,
    confirmedAttendees: confirmedAttendees(guests),
  };

  if (authLoading || loadingInvitation) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!invitation) {
    return (
      <Layout>
        <div className="container mx-auto py-8 px-4 text-center">
          <p>Invitación no encontrada</p>
          <Button asChild className="mt-4">
            <Link to="/dashboard">Volver al Dashboard</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-display font-bold">
                Invitados de {invitation.bride_name} & {invitation.groom_name}
              </h1>
              <p className="text-muted-foreground text-sm">
                Vista rápida de invitados para esta invitación
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Importar CSV
            </Button>
            <Button variant="outline" asChild>
              <Link to={`/invitations/${id}/edit`}>
                Editar Invitación
              </Link>
            </Button>
            <Button asChild>
              <Link to="/dashboard/guests">
                <UserPlus className="w-4 h-4 mr-2" />
                Gestionar Invitados
              </Link>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Invitaciones</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.confirmed}</p>
                  <p className="text-xs text-muted-foreground">Confirmados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{stats.confirmedAttendees}</p>
                  <p className="text-xs text-muted-foreground">Asistentes confirmados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Banner */}
        <Card className="mb-6 border-primary/20 bg-primary/5">
          <CardContent className="py-4">
            <p className="text-sm text-center">
              Para agregar, editar o eliminar invitados, utiliza el{' '}
              <Link to="/dashboard/guests" className="text-primary font-medium underline">
                Gestor de Invitados centralizado
              </Link>
            </p>
          </CardContent>
        </Card>

        {/* Guest List (Read-only) */}
        {loadingGuests ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : guests && guests.length > 0 ? (
          <div className="space-y-4">
            {guests.map((guest) => (
              <Card key={guest.id}>
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{guest.guest_name}</h3>
                        <Badge 
                          variant={
                            guest.rsvp_status === 'confirmed' ? 'default' :
                            guest.rsvp_status === 'declined' ? 'destructive' :
                            'secondary'
                          }
                        >
                          {guest.rsvp_status === 'confirmed' && 'Confirmado'}
                          {guest.rsvp_status === 'declined' && 'No asiste'}
                          {guest.rsvp_status === 'pending' && 'Pendiente'}
                        </Badge>
                        {guest.rsvp_status === 'confirmed' && guest.rsvp_guests_count > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {guest.rsvp_guests_count} persona{guest.rsvp_guests_count > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                        {guest.guest_email && <p>{guest.guest_email}</p>}
                        {guest.guest_phone && <p>{guest.guest_phone}</p>}
                        <p>Pases: {guest.passes_count}</p>
                        {guest.rsvp_dietary_restrictions && (
                          <p className="text-amber-600">🍽️ {guest.rsvp_dietary_restrictions}</p>
                        )}
                        {guest.rsvp_message && (
                          <p className="italic text-muted-foreground">💬 "{guest.rsvp_message}"</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyInviteLink(guest)}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copiar Link
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                      >
                        <a 
                          href={`/i/${invitation.unique_slug}?code=${guest.unique_access_code}`} 
                          target="_blank"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                      {guest.guest_phone && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-600"
                          onClick={() => shareViaWhatsApp(guest)}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No hay invitados</h3>
              <p className="text-muted-foreground mb-4">
                Agrega invitados desde el gestor centralizado
              </p>
              <Button asChild>
                <Link to="/dashboard/guests">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Ir al Gestor de Invitados
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {invitation && (
        <CSVImportModal
          open={importOpen}
          onOpenChange={setImportOpen}
          invitations={[{
            id: invitation.id,
            bride_name: invitation.bride_name,
            groom_name: invitation.groom_name,
          }]}
          defaultInvitationId={invitation.id}
        />
      )}
    </Layout>
  );
};

export default GuestManager;
